import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

/**
 * A custom hook that triggers validation for a field when its required status changes.
 *
 * This hook monitors a form field for "required" validation errors. If a field has a required error
 * but is no longer required, it automatically triggers validation for that field to clear the error
 * state.
 *
 * @param {Object} params - The parameters for the hook
 * @param {string} params.name - The name of the form field to monitor
 * @param {boolean} [params.isRequired] - Whether the field is currently required
 * @returns {void}
 */
export const useTriggerRequiredError = ({
  name,
  isRequired,
}: {
  name: string;
  isRequired?: boolean;
}): void => {
  const { trigger, getFieldState } = useFormContext();

  const hasRequiredError = getFieldState(name).error?.type === 'required';

  useEffect(() => {
    if (hasRequiredError && !isRequired) {
      // Field has error but is not required anymore, trigger validation
      trigger(name);
    }
  }, [name, isRequired, trigger, hasRequiredError]);
};
